import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Calendar, CaretLeft, CaretRight, Star } from '@phosphor-icons/react';
import { getCategoryLabel } from '../lib/categories';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function FeaturedCarousel() {
  const [sales, setSales] = useState([]);
  const [index, setIndex] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/featured-sales`)
      .then(({ data }) => setSales(data.sales || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (sales.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setIndex(prev => (prev + 1) % Math.max(1, sales.length - 2));
    }, 4000);
    return () => clearInterval(intervalRef.current);
  }, [sales.length]);

  const prev = () => { clearInterval(intervalRef.current); setIndex(i => Math.max(0, i - 1)); };
  const next = () => { clearInterval(intervalRef.current); setIndex(i => Math.min(sales.length - 3, i + 1)); };

  if (sales.length === 0) return null;

  const visible = sales.slice(index, index + 3);

  return (
    <div className="relative" data-testid="featured-carousel">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-xl font-bold text-[#004E89]" style={{ fontFamily: 'Outfit' }}>Featured Sales</h3>
        <div className="flex-1 h-0.5 bg-[#004E89] bg-opacity-15 rounded" />
        {sales.length > 3 && (
          <div className="flex gap-1.5">
            <button onClick={prev} disabled={index === 0}
              className="w-7 h-7 rounded-lg border-2 border-[#004E89] flex items-center justify-center bg-white disabled:opacity-30 hover:bg-[#FFD166] transition-colors shadow-[2px_2px_0px_#004E89]"
              data-testid="carousel-prev">
              <CaretLeft size={14} weight="bold" className="text-[#004E89]" />
            </button>
            <button onClick={next} disabled={index >= sales.length - 3}
              className="w-7 h-7 rounded-lg border-2 border-[#004E89] flex items-center justify-center bg-white disabled:opacity-30 hover:bg-[#FFD166] transition-colors shadow-[2px_2px_0px_#004E89]"
              data-testid="carousel-next">
              <CaretRight size={14} weight="bold" className="text-[#004E89]" />
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map(sale => (
          <Link key={sale.id} to={`/sale/${sale.id}`} className="block" data-testid={`featured-sale-${sale.id}`}>
            <div className="bg-white border-2 border-[#004E89] rounded-2xl overflow-hidden shadow-[4px_4px_0px_#004E89] hover:-translate-y-1 transition-transform">
              {sale.photos?.length > 0 ? (
                <div className="h-36 overflow-hidden">
                  <img src={`${API}/files/${sale.photos[0]}`} alt={sale.title} className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://images.pexels.com/photos/4923440/pexels-photo-4923440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'; }} />
                </div>
              ) : (
                <div className="h-36 bg-gradient-to-br from-[#FFD166] to-[#FF6B35] flex items-center justify-center">
                  <MapPin size={36} weight="duotone" color="#004E89" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className={`tag text-[10px] tag-${sale.category === 'business_closing' ? 'business' : ['craft_fair','food_vendor','carnival','vendor_event'].includes(sale.category) ? 'vendor' : sale.category}`}>
                    {getCategoryLabel(sale.category)}
                  </span>
                  {sale.avg_rating > 0 && (
                    <div className="flex items-center gap-0.5">
                      <Star size={12} weight="fill" className="text-[#FFD166]" />
                      <span className="text-[10px] font-bold text-[#004E89]">{sale.avg_rating}</span>
                    </div>
                  )}
                </div>
                <h4 className="text-sm font-bold text-[#004E89] truncate" style={{ fontFamily: 'Outfit' }}>{sale.title}</h4>
                <p className="text-xs text-[#5C7A99] flex items-center gap-1 mt-1">
                  <MapPin size={12} weight="bold" /> {sale.city}, {sale.state}
                </p>
                <p className="text-xs text-[#5C7A99] flex items-center gap-1">
                  <Calendar size={12} weight="bold" /> {new Date(sale.start_date).toLocaleDateString()}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
